import { useEffect, useRef, useState } from "react";

import { useFetchAccountDetails } from "@/lib/api/routes/account/queries";
import { dd } from "@/helpers/datadog";

import {
  LOOKALIKE_SYNC_POLL_MS,
  LookalikeSyncStatus,
  resolveLookalikeSyncStatus,
} from "../utils/lookalike-meeting-sync";

interface UseLookalikeMeetingSyncArgs {
  campaignId: string | undefined;
  accountId: string | undefined;
  /**
   * Fetch account-details and capture the pre-booking baseline. Enable this
   * from modal open on lookalike campaigns (before the SDR books) so the
   * baseline reflects the meeting state prior to this booking.
   */
  enabled: boolean;
  /** Detection is running (the SDR reached AutoSyncStep) — poll + run the clock. */
  active: boolean;
}

/**
 * Watches whether the lookalike/ICS channel has synced the meeting for this
 * account, so AutoSyncStep can report the truth (detecting / found / timeout)
 * instead of optimistically claiming success. Polls the existing
 * account-details endpoint and inspects `account_status.bmce.ics_uid`.
 */
export const useLookalikeMeetingSync = ({
  campaignId,
  accountId,
  enabled,
  active,
}: UseLookalikeMeetingSyncArgs): {
  status: LookalikeSyncStatus;
  meetingTime: string | null;
  /** GLE-8421: the synced meeting's activity_log id; null until "found". */
  activityLogId: string | null;
} => {
  // undefined = not captured yet; null = no prior ICS meeting.
  const baselineRef = useRef<string | null | undefined>(undefined);
  const startedAtRef = useRef<number | null>(null);
  // Guards the one-shot terminal telemetry (found/timeout) against effect re-runs.
  const terminalEmittedRef = useRef(false);
  const [isResolved, setIsResolved] = useState(false);

  const query = useFetchAccountDetails(
    campaignId,
    accountId,
    enabled,
    active && !isResolved ? LOOKALIKE_SYNC_POLL_MS : undefined
  );

  const bmce = query.data?.data?.account_status?.bmce ?? null;

  // Capture the baseline ics_uid once, from the first load (modal open).
  useEffect(() => {
    if (enabled && baselineRef.current === undefined && query.isSuccess) {
      baselineRef.current = bmce?.ics_uid ?? null;
    }
  }, [enabled, query.isSuccess, bmce?.ics_uid]);

  // Start/reset the detection clock as the SDR enters/leaves AutoSyncStep.
  useEffect(() => {
    if (active) {
      if (startedAtRef.current === null) {
        startedAtRef.current = Date.now();
        dd.rum.log("lookalike_detect.ics.started", {
          campaign_id: campaignId,
          account_id: accountId,
          tier: "ics",
        });
      }
    } else {
      startedAtRef.current = null;
      terminalEmittedRef.current = false;
      setIsResolved(false);
    }
  }, [active, campaignId, accountId]);

  const baselineIcsUid = baselineRef.current ?? null;
  const elapsedMs =
    active && startedAtRef.current !== null
      ? Date.now() - startedAtRef.current
      : 0;

  const status: LookalikeSyncStatus = active
    ? resolveLookalikeSyncStatus({ bmce, baselineIcsUid, elapsedMs })
    : "detecting";

  const meetingTime =
    bmce?.start_time ?? query.data?.data?.account_status?.meeting_time ?? null;

  // Stop polling once a terminal state is reached, and emit one-shot telemetry
  // so a booking's ICS outcome (found vs 5-min timeout) is visible in RUM.
  useEffect(() => {
    if (active && (status === "found" || status === "timeout")) {
      setIsResolved(true);

      if (!terminalEmittedRef.current) {
        terminalEmittedRef.current = true;
        dd.rum.log(`lookalike_detect.ics.${status}`, {
          campaign_id: campaignId,
          account_id: accountId,
          tier: "ics",
          ...(status === "found" ? { meeting_time: meetingTime } : {}),
        });
      }
    }
  }, [active, status, campaignId, accountId, meetingTime]);

  // Before "found" bmce can be the baseline meeting (the one a reschedule
  // replaces), so its id must not leak out.
  const activityLogId =
    status === "found" ? (bmce?.activity_log_id ?? null) : null;

  return { status, meetingTime, activityLogId };
};
